import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CustomCellEditorProps } from 'ag-grid-react';
import { Search, Globe, Loader2, Package, Box, Trash2, AlertTriangle } from 'lucide-react';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuth } from '@/hooks/useAuth';

interface SearchResultItem {
    id: string; // MATNR
    description: string; // MAKTX
    label?: string;
    rawId?: string;
}

// Los MATNR de SAP son numéricos (hasta 18 dígitos, normalmente con ceros a la
// izquierda). Algunas cargas masivas antiguas (ej. país "Spain (YN)") dejaron
// texto descriptivo del producto en este campo en vez del código real; eso no
// es un código SAP válido.
const isPlausibleSapCode = (v: string) => /^\d+$/.test(v) && v.length <= 18;

export const MaterialCellEditor = forwardRef((props: CustomCellEditorProps, ref) => {
    const { user } = useAuth();

    const valueRef = useRef(props.value);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Solo usamos value para inicializar, no necesitamos setValue para renderizar
    // cambios rápidos antes de cerrar el editor.
    const [searchTerm, setSearchTerm] = useState('');
    const [sapResults, setSapResults] = useState<SearchResultItem[]>([]);
    const [isSearchingSAP, setIsSearchingSAP] = useState(false);

    const appId = typeof window !== 'undefined' && (window as any).__app_id ? (window as any).__app_id : 'default-app-id';

    useImperativeHandle(ref, () => ({
        getValue: () => valueRef.current,
        isPopup: () => false,
    }));

    useEffect(() => {
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                // No precargamos el buscador con el valor actual si no parece un
                // código SAP real: lanzaría una búsqueda contra SAP que nunca
                // encontrará nada y confundiría al usuario. Dejamos el campo
                // vacío para que busque el material correcto desde cero.
                if (props.value && isPlausibleSapCode(String(props.value))) {
                    setSearchTerm(String(props.value));
                }
            }
        }, 50);
    }, [props.value]);

    const currentValueLooksInvalid = !!props.value && !isPlausibleSapCode(String(props.value));

    useEffect(() => {
        if (!user?.uid || !appId) return;

        const handler = setTimeout(async () => {
            if (searchTerm.trim().length >= 2) {
                setIsSearchingSAP(true);
                const searchDocRef = doc(db, 'artifacts', appId, 'users', user.uid, 'frontEnd', 'materialSearch');
                await setDoc(searchDocRef, {
                    searchTerm: searchTerm,
                    timestamp: new Date().toISOString(),
                    searchResults: null // Reset results to keep loader active
                }, { merge: true });
            } else if (searchTerm.length === 0) {
                setSapResults([]);
            }
        }, 600);

        return () => clearTimeout(handler);
    }, [searchTerm, user, appId]);

    useEffect(() => {
        if (!user?.uid || !appId) return;

        const searchDocRef = doc(db, 'artifacts', appId, 'users', user.uid, 'frontEnd', 'materialSearch');
        const unsubscribe = onSnapshot(searchDocRef, (docSnap) => {
            const data = docSnap.data();
            if (data && data.searchResults) {
                const resultsArray = Object.values(data.searchResults) as SearchResultItem[];
                setSapResults(resultsArray);
                setIsSearchingSAP(false);
            }
        });

        return () => unsubscribe();
    }, [user, appId]);

    // --- CORRECCIÓN AQUÍ ---
    const handleSelect = (materialId: string) => {
        // 1. Actualizar la referencia del valor para AG Grid
        // Esto es lo que getValue leerá al cerrarse el editor
        valueRef.current = materialId;
        // Con reactiveCustomComponents=true, AG Grid solo detecta el cambio si se
        // notifica explícitamente vía onValueChange (getValue por ref ya no se usa).
        props.onValueChange(materialId);

        // Opcional: Si quisieras guardar la descripción en otra columna (ej. 'notes'), lo harías aquí:
        // if (props.node && description) {
        //     props.node.setDataValue('notes', description);
        // }

        // 2. Cerrar el editor inmediatamente
        props.stopEditing();
    };

    const handleManualEntry = () => {
        if (searchTerm.trim()) {
            valueRef.current = searchTerm;
            props.onValueChange(searchTerm);
            props.stopEditing();
        }
    };

    const handleClear = () => {
        valueRef.current = '';
        props.onValueChange('');
        props.stopEditing();
    };

    const editorContent = (
        <div
            ref={containerRef}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/5 backdrop-blur-[2px] font-sans"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
                if (e.target === e.currentTarget) props.stopEditing();
            }}
        >
            <div className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 ring-1 ring-black/5" style={{ width: '500px', maxHeight: '65vh' }}>
                <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3 bg-gray-50">
                    <Search className="h-4 w-4 text-gray-400" />
                    <input
                        ref={inputRef}
                        type="text"
                        className="flex-1 bg-transparent border-none text-base focus:outline-none focus:ring-0 placeholder:text-gray-400 text-gray-800 font-medium"
                        placeholder="Search material..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.stopPropagation();
                                if (sapResults.length > 0) {
                                    handleSelect(sapResults[0].id);
                                } else {
                                    handleManualEntry();
                                }
                            }
                            if (e.key === 'Escape') props.stopEditing();
                        }}
                    />
                    {isSearchingSAP && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
                </div>

                {currentValueLooksInvalid && (
                    <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-700 flex items-start gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>
                            El valor actual no parece un código SAP válido: <span className="font-mono break-all">{String(props.value)}</span>.
                            Busca y selecciona el material correcto, o usa "Clear" si no lo conoces.
                        </span>
                    </div>
                )}

                <ul className="overflow-y-auto flex-1 p-2 list-none m-0 bg-white">
                    {isSearchingSAP ? (
                        <div className="flex flex-col items-center justify-center py-8 text-gray-400 gap-2">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                            <span className="text-sm">Searching in SAP...</span>
                        </div>
                    ) : sapResults.length > 0 ? (
                        <div className="mb-2">
                            <div className="px-3 py-1 text-[10px] font-bold text-blue-500 uppercase tracking-wider flex items-center gap-1 mb-1"><Globe className="w-3 h-3" /> SAP Results</div>
                            {sapResults.map((mat) => (
                                <li
                                    key={mat.id}
                                    className="px-3 py-2.5 text-sm rounded-lg cursor-pointer transition-colors mb-0.5 text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex flex-col gap-0.5 group"
                                    onClick={() => handleSelect(mat.id)}
                                >
                                    <div className="flex justify-between items-center w-full">
                                        <span className="font-semibold text-gray-900 group-hover:text-blue-700">
                                            {String(mat.id).replace(/^0+/, '')}
                                        </span>
                                        <Package className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-300" />
                                    </div>
                                    <span className="text-xs text-gray-500 group-hover:text-blue-600 truncate">{mat.description || 'No description'}</span>
                                </li>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-8 text-gray-400 gap-2">
                            <Box className="w-8 h-8 opacity-20" />
                            <span className="text-sm">No materials found via SAP</span>
                            <button onClick={handleManualEntry} className="mt-2 text-xs text-blue-600 hover:underline font-medium">Use "{searchTerm}" as manual entry</button>
                        </div>
                    )}
                </ul>
                <div className="px-4 py-2 border-t border-gray-50 bg-white flex justify-between items-center text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                    <span>{sapResults.length} results found</span>
                    <div className="flex items-center gap-3">
                        <button
                            onMouseDown={(e) => { e.preventDefault(); handleClear(); }}
                            className="flex items-center gap-1 hover:text-red-500 cursor-pointer transition-colors"
                        >
                            <Trash2 className="w-3 h-3" /> Clear
                        </button>
                        <div className="h-4 w-px bg-gray-200 mx-1"></div>
                        <span className="flex items-center gap-1"><kbd className="font-sans bg-gray-100 px-1 rounded text-gray-500">↵</kbd> Select</span>
                        <span className="flex items-center gap-1"><kbd className="font-sans bg-gray-100 px-1 rounded text-gray-500">Esc</kbd> Close</span>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(editorContent, document.body);
});

MaterialCellEditor.displayName = 'MaterialCellEditor';